import { useContext } from 'react'

import { useForm, useLatestEntity, useThunkDispatch } from '@domain-group/react-hooks'
import type { UseFormOptions, UseFormPayload } from '@domain-group/react-hooks'

import PageContext from '@contexts/pageContext'

import { digObject } from '@domain-group/javascript-utils'

import formatDate from '@functions/formatDate'

import useSelector from '@hooks/useSelector'

import * as orderBundleActions from '@redux/modules/orderBundle'
import type { AppDispatch } from '@redux/store'
import type { EntityOptions } from '@redux/modules/types'

import defaultState, { requiredFields } from '@models/orderBundle'
import type { OrderBundleModel } from '@models/types'

type GenerateUrlsOptions = {
  prefixRoute?: string,
}

export const generateUrls = (orderBundle: Partial<OrderBundleModel> = {}, options: GenerateUrlsOptions = {}) => {
  const { id } = orderBundle
  const { prefixRoute = 'systemManager' } = options || {}

  return {
    editOrderBundleRelationsUrl: `#/${prefixRoute}/orderBundles/${id}/edit/relations`,
    editOrderBundleUrl: `#/${prefixRoute}/orderBundles/${id}/edit`,
    hiddenOrderBundlesIndexUrl: `#/${prefixRoute}/orderBundles?hidden=true`,
    orderBundlesIndexUrl: `#/${prefixRoute}/orderBundles`,
  }
}

type CreateOrderBundlePayload = {
  orderBundleParams: Partial<OrderBundleModel>,
  dispatch: AppDispatch,
  requestOptions?: EntityOptions,
}

const createOrderBundle = (params: CreateOrderBundlePayload) => {
  const { orderBundleParams, dispatch, requestOptions } = params
  const { createOrderBundle: createFn } = orderBundleActions

  // @ts-expect-error TS2345
  return dispatch(createFn(orderBundleParams, requestOptions)).then((response) => {
    const { success } = response

    if (success){
      const { data } = response
      const { editOrderBundleUrl } = generateUrls(data)

      return {
        success,
        data,
        redirectUrl: editOrderBundleUrl,
      }
    }

    return response
  })
}

type UpdateOrderBundlePayload = {
  dispatch: AppDispatch,
  orderBundle: OrderBundleModel,
  orderBundleParams: Partial<OrderBundleModel>,
  requestOptions?: EntityOptions,
}

const updateOrderBundle = (params: UpdateOrderBundlePayload) => {
  const {
    orderBundle, orderBundleParams, dispatch, requestOptions,
  } = params

  const { updateOrderBundle: updateFn } = orderBundleActions

  const updatedParams = {
    id: orderBundle.id,
    ...orderBundleParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DuplicateOrderBundlePayload = {
  dispatch: AppDispatch,
  orderBundleParams: Partial<OrderBundleModel>,
  orderBundle: OrderBundleModel,
  requestOptions?: EntityOptions,
}

const duplicateOrderBundle = (params: DuplicateOrderBundlePayload) => {
  const {
    dispatch, orderBundle, orderBundleParams, requestOptions,
  } = params

  const { duplicateOrderBundle: duplicateFn } = orderBundleActions

  const updatedParams = {
    id: orderBundle.id,
    ...orderBundleParams,
  }

  // @ts-expect-error TS2345
  return dispatch(duplicateFn(updatedParams, requestOptions)).then((response) => {
    const { success } = response

    if (success){
      const { data } = response
      const { id } = data

      const { editOrderBundleUrl } = generateUrls({ id })

      return {
        data,
        redirectUrl: editOrderBundleUrl,
        success,
      }
    }

    return response
  })
}

export function useOrderBundleForm(
  orderBundle: Partial<OrderBundleModel> = {},
  options: UseFormOptions = {},
): UseFormPayload {
  const { validateOn } = options

  const endDate = digObject(orderBundle, 'dates.end.date', '')
  const startDate = digObject(orderBundle, 'dates.start.date', '')

  const end_date = formatDate(endDate, 'dd/MM/yyyy', 'yyyy-MM-dd')
  const start_date = formatDate(startDate, 'dd/MM/yyyy', 'yyyy-MM-dd')

  const orderBundleForm = useForm(
    defaultState,
    {
      entity: { ...orderBundle, end_date, start_date },
      requiredFields,
      validateOn,
    },
    [orderBundle.id, orderBundle.cache_key],
  )

  return {
    ...orderBundleForm,
  }
}

type LaunchDuplicateOrderBundleModal = {
  orderBundle: Partial<OrderBundleModel>,
  showDuplicateOrderBundleModal: Function,
}

const launchDuplicateOrderBundleModal = (params: LaunchDuplicateOrderBundleModal) => new Promise((resolve, reject) => {
  const { orderBundle, showDuplicateOrderBundleModal } = params

  if (showDuplicateOrderBundleModal){
    const payload = {
      orderBundle,
    }

    showDuplicateOrderBundleModal(payload)

    resolve({ success: true, result: payload })
  }

  reject(new Error('showDuplicateOrderBundleModal not defined in PageContext callbacks'))
})

export type UseOrderBundlePayload = {
  callbacks: {
    createOrderBundle: (orderBundleParams: Partial<OrderBundleModel>, requestOptions?: EntityOptions) => void,
    duplicateOrderBundle: (orderBundleParams: Partial<OrderBundleModel>, requestOptions?: EntityOptions) => void,
    launchDuplicateOrderBundleModal: () => void,
    updateOrderBundle: (orderBundleParams: Partial<OrderBundleModel>, requestOptions?: EntityOptions) => void,
  },
  creating: boolean,
  loading: boolean,
  orderBundle: OrderBundleModel,
  updating: boolean,
  urls: object,
}

function useOrderBundle(initEntity: Partial<OrderBundleModel> = {}): UseOrderBundlePayload {
  const { entity: orderBundle } = useLatestEntity(initEntity, 'orderBundles')

  const dispatch = useThunkDispatch()

  const { creating, loading, updating } = useSelector(reduxState => reduxState.orderBundles)

  const { callbacks } = useContext(PageContext)
  const { showDuplicateOrderBundleModal } = callbacks || {}

  return {
    callbacks: {
      createOrderBundle: (orderBundleParams: Partial<OrderBundleModel>, requestOptions?: EntityOptions) => (
        createOrderBundle({ orderBundleParams, dispatch, requestOptions })
      ),
      duplicateOrderBundle: (orderBundleParams: Partial<OrderBundleModel>, requestOptions?: EntityOptions) => (
        duplicateOrderBundle({
          // @ts-expect-error TS2322
          orderBundle, orderBundleParams, dispatch, requestOptions,
        })
      ),
      launchDuplicateOrderBundleModal: () => launchDuplicateOrderBundleModal({
        orderBundle,
        showDuplicateOrderBundleModal,
      }),
      updateOrderBundle: (
        orderBundleParams: Partial<OrderBundleModel>,
        requestOptions?: EntityOptions,
      ) => (
        updateOrderBundle({
          // @ts-expect-error TS2322
          orderBundle, orderBundleParams, dispatch, requestOptions,
        })
      ),
    },
    creating,
    loading,
    // @ts-expect-error TS2322
    orderBundle,
    updating,
    urls: generateUrls(orderBundle),
  }
}

export default useOrderBundle
